import type { ConfigChangeSummary } from '@vivport/backend/types';
import { AppLink } from '@/components/ui/app-link';
import { toContentHref } from '@/utils/content-ref';

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

interface ProvenanceAttributionProps {
  lastChange: ConfigChangeSummary | null | undefined;
  fallbackDate?: string;
}

export function ProvenanceAttribution({ lastChange, fallbackDate }: ProvenanceAttributionProps) {
  if (!lastChange) {
    if (fallbackDate) {
      return (
        <span className="text-xs text-muted-foreground">
          Updated {new Date(fallbackDate).toLocaleDateString()}
        </span>
      );
    }
    return null;
  }

  const time = relativeTime(lastChange.changedAt);

  if (lastChange.role === 'MEMBER') {
    return (
      <span className="text-xs text-muted-foreground">
        Modified by you · {time}
      </span>
    );
  }

  if (lastChange.role === 'ASSISTANT') {
    return (
      <span className="text-xs text-muted-foreground">
        Modified by Neo · {time} ·{' '}
        <AppLink
          to={toContentHref(lastChange.provenanceCode)}
          className="underline hover:text-foreground"
          onClick={(e) => e.stopPropagation()}
        >
          in NeoChat
        </AppLink>
      </span>
    );
  }

  return (
    <span className="text-xs text-muted-foreground">
      Modified by Process · {time} ·{' '}
      <AppLink
        to={toContentHref(lastChange.provenanceCode)}
        className="underline hover:text-foreground"
        onClick={(e) => e.stopPropagation()}
      >
        in ProcessRun
      </AppLink>
    </span>
  );
}
